/**
 * main.tsx — frontend-v3 entry point (T304 backend-bound provider tree).
 *
 * Provider order (load-bearing):
 *   AppErrorBoundary
 *     └─ Auth0Provider                    (owns the browser session)
 *         └─ ConvexProviderWithAuth0      (puts its ID token on the Convex socket)
 *             ├─ RouterProvider           (the route tree from src/routes)
 *             └─ Toaster                  (Sonner — sibling, inside the provider)
 *
 * NMW-1239: Auth0 replaced Convex Auth as the console's identity provider.
 * `ConvexProviderWithAuth0` is Convex's own adapter for `@auth0/auth0-react`
 * (convex/react-auth0) — it hands the deployment the Auth0 ID TOKEN, which is
 * what convex/auth.config.ts verifies (`applicationID` = the SPA client id).
 * There is no hand-written bridge and no access-token audience.
 *
 * AppErrorBoundary is the outermost ring so a provider-construction error
 * surfaces as the full-screen 500 card rather than a blank page. Toaster is
 * a sibling of RouterProvider inside ConvexProvider so toasts can subscribe
 * to Convex queries (e.g. an action-failed toast referencing live data).
 *
 * Two window-level error listeners forward uncaught failures into the
 * structured logger so even non-React errors land in the observability pipe.
 *
 * T304 PORT-SITE FIX #2 — extended, did NOT clobber, the v.3 `main.tsx`
 * authored by T001. The mock-only boot has been replaced with the backend-
 * bound provider tree; the route table from `./routes` is unchanged.
 */
import React from 'react';
import ReactDOM from 'react-dom/client';
import { Auth0Provider } from '@auth0/auth0-react';
import type { AppState } from '@auth0/auth0-react';
import { ConvexProviderWithAuth0 } from 'convex/react-auth0';
import { RouterProvider } from 'react-router-dom';
import { convex } from './lib/convex';
import { env } from './lib/env';
import { AppErrorBoundary } from './layouts/AppErrorBoundary';
import { ThemeProvider, ThemedToaster } from './lib/theme';
import { router } from './routes';
import { logger } from './lib/logger';
import './styles/global.css';

window.addEventListener('unhandledrejection', (event) => {
  logger.error('unhandled_promise_rejection', {
    reason:
      event.reason instanceof Error
        ? event.reason.message
        : String(event.reason),
  });
});

window.addEventListener('error', (event) => {
  logger.error('window_error', {
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
  });
});

/**
 * `onRedirectCallback` for Auth0Provider. Runs once, after Auth0 has exchanged
 * the code in the callback URL.
 *
 * WHY IT EXISTS. Auth0 must return the browser to a registered origin, so
 * `redirect_uri` is the bare origin — which on its own would drop an operator
 * who deep-linked to `/channels/abc` on the root route after signing in. The
 * sign-in screen stashes the route it came from in `appState.returnTo`
 * (src/screens/auth/_bundle.tsx) and this puts it back before the router
 * mounts and reads the URL.
 *
 * The `replaceState` also strips `?code=&state=` from the address bar, which
 * is what the provider's own default would have done — supplying a callback
 * REPLACES that default rather than adding to it.
 */
function restoreRouteAfterLogin(appState?: AppState): void {
  const returnTo =
    typeof appState?.returnTo === 'string' && appState.returnTo.startsWith('/')
      ? appState.returnTo
      : '/';
  window.history.replaceState({}, '', returnTo);
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element #root not found in document');
}

// ThemeProvider (NMW-246) owns light/dark/system and sets <html data-theme>.
// ThemedToaster reads the resolved theme so Sonner toasts match the palette.
const app = (
  <ThemeProvider>
    <RouterProvider router={router} />
    <ThemedToaster />
  </ThemeProvider>
);

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <AppErrorBoundary>
      <Auth0Provider
        domain={env.AUTH0_DOMAIN}
        clientId={env.AUTH0_CLIENT_ID}
        authorizationParams={{ redirect_uri: window.location.origin }}
        // Rotating, expiring refresh tokens (infra/auth0) only survive a
        // reload if they are cached somewhere the reload can still read.
        useRefreshTokens
        useRefreshTokensFallback={false}
        cacheLocation="localstorage"
        onRedirectCallback={restoreRouteAfterLogin}
      >
        <ConvexProviderWithAuth0 client={convex}>{app}</ConvexProviderWithAuth0>
      </Auth0Provider>
    </AppErrorBoundary>
  </React.StrictMode>,
);
